import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismButton, PrismCard, PrismIcon } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { StepIndicator } from 'frontend-utils';
import {
  INTEGRATION_STATUS,
  INTEGRATION_STATUS_STYLES,
  type IntegrationStatus,
} from 'lumen/services/benchmark-insight/integration-status';

interface HotelBenchmarkInsightCardIntegrationStatusSignature {
  Args: {
    dismissCardIntegrationStatus: () => void;
    integrationStatus: IntegrationStatus;
  };
}

export default class HotelBenchmarkInsightCardIntegrationStatus extends Component<HotelBenchmarkInsightCardIntegrationStatusSignature> {
  @service declare router: Services['router'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get currentStep() {
    const { integrationStatus } = this.args;

    if (
      integrationStatus === INTEGRATION_STATUS.SETUP_COMPLETE_COMPSET ||
      integrationStatus === INTEGRATION_STATUS.SETUP_COMPLETE_DESIRED_COMPSET
    ) {
      return 5;
    }

    return Object.values(INTEGRATION_STATUS).indexOf(integrationStatus) + 1;
  }

  get currentStepHasBoldText(): boolean {
    const stepsWithBoldText: Partial<IntegrationStatus[]> = [
      INTEGRATION_STATUS.MAPPING_NEEDED_INFO,
      INTEGRATION_STATUS.MAPPING_NEEDED_WARNING,
    ];

    return stepsWithBoldText.includes(this.args.integrationStatus);
  }

  get currentStepIsDismissable(): boolean {
    const dismissableSteps: Partial<IntegrationStatus[]> = [
      INTEGRATION_STATUS.SETUP_COMPLETE_COMPSET,
      INTEGRATION_STATUS.SETUP_COMPLETE_DESIRED_COMPSET,
    ];

    return dismissableSteps.includes(this.args.integrationStatus);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get integrationStatusStyle() {
    return INTEGRATION_STATUS_STYLES[this.args.integrationStatus];
  }

  @action
  onClickAction(): void {
    this.integrationStatusStyle.action?.(this.router);
  }

  <template>
    <PrismCard data-test-id="card-integration-status">
      <:header as |Header|>
        <Header.title>
          <div
            class="flex flex-row items-center gap-200"
            data-test-id="card-integration-status-title"
          >
            <PrismIcon
              @color={{if
                this.integrationStatusStyle.iconColor
                this.integrationStatusStyle.iconColor
                "default"
              }}
              @name={{this.integrationStatusStyle.icon}}
              data-test-id="card-integration-status-icon"
            />

            {{t this.integrationStatusStyle.title}}
          </div>
        </Header.title>
      </:header>

      <:headerAfter>
        {{#if this.currentStepIsDismissable}}
          <PrismButton
            @buttonType="ghost"
            @icon="cross"
            @onClick={{@dismissCardIntegrationStatus}}
            data-test-id="card-integration-status-dismiss"
          />
        {{/if}}
      </:headerAfter>

      <:content>
        <div
          class="flex flex-col gap-400"
          data-test-id="card-integration-status-content"
        >
          <StepIndicator
            @color="success"
            @currentStep={{this.currentStep}}
            @size="small"
            @steps={{5}}
          />

          <p class="text-300--regular">
            {{#if this.currentStepHasBoldText}}
              {{t
                this.integrationStatusStyle.description
                classNameBold="text-300--bold"
                htmlSafe=true
              }}
            {{else}}
              {{t this.integrationStatusStyle.description}}
            {{/if}}
          </p>

          {{#if this.integrationStatusStyle.action}}
            <div class="w-fit">
              <PrismButton
                @buttonType="secondary"
                @label={{t this.integrationStatusStyle.actionLabel}}
                @onClick={{this.onClickAction}}
                data-test-id="card-integration-status-action"
              />
            </div>
          {{/if}}
        </div>
      </:content>
    </PrismCard>
  </template>
}
